import type { GamifyTrialExtensionResponse } from '@heidi/commerce/contracts';
import type { TranslateFunction } from '@heidi/runtime/i18n';
// Type-only, deliberately: a value import of react-intl here would break the
// suites that mock the module with a bespoke factory (see intl-bridge.ts for
// the same reasoning).
import type { IntlShape } from 'react-intl';
import { trialExtensionProgressMessages as messages } from './trialExtensionProgress.messages';

export type TrialExtensionProgress = {
  completedCount: number;
  totalCount: number;
  completedActions: number;
  totalActions: number;
  /** Task-level progress percentage (0–100). */
  progressPercent: number;
  allTasksComplete: boolean;
  /** At least one action started but zero tasks fully complete. */
  hasStartedActions: boolean;
};

export function getTrialExtensionProgress(
  data: GamifyTrialExtensionResponse | undefined | null
): TrialExtensionProgress {
  const tasks = data?.tasks ?? [];
  const completedCount = tasks.filter((t) => t.completed).length;
  const totalCount = tasks.length;
  const completedActions = tasks.reduce((sum, t) => sum + t.current, 0);
  const totalActions = tasks.reduce((sum, t) => sum + t.target, 0);
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allTasksComplete =
    (data?.all_tasks_complete ?? false) || (data?.trial_extended ?? false);
  const hasStartedActions = completedActions > 0 && completedCount === 0;

  return {
    completedCount,
    totalCount,
    completedActions,
    totalActions,
    progressPercent,
    allTasksComplete,
    hasStartedActions,
  };
}

// ---------------------------------------------------------------------------
// Copy helpers.
//
// EVERY helper below dispatches on `getTrialExtensionPhase`, and none of them
// reads a count to decide WHICH string to use. Counts appear only as
// interpolation values. That is the point: while each helper branched on its
// own arithmetic, two helpers could classify the same state differently — at
// 2 tasks / 1 done the title said "One step from 14 free days" while the body
// said "finish the remaining tasks", because the title keyed off
// `completedCount === totalCount - 1` and the body off `completedCount === 1`.
// One phase, read by all, makes that unrepresentable.
//
// The `*2Of3` / `*1Of3` key names are historical — their English says "the last
// task" / "1 task left", which is what the phases now mean.
// ---------------------------------------------------------------------------

export type TrialExtensionPhase =
  | 'earned'
  | 'notStarted'
  | 'actionsStarted'
  | 'oneLeft'
  | 'inProgress';

/**
 * Single source of "where is this user in the checklist". Every copy helper
 * reads this, so title, body, hover card and CTA cannot disagree.
 *
 * `oneLeft` sits below the `completedCount === 0` branches, so a one-task
 * checklist with nothing done can never be "one step away" — the guard is
 * structural, not a `completedCount > 0` each caller has to remember.
 *
 * `hasStartedActions` is already defined as
 * `completedActions > 0 && completedCount === 0`, so inside the
 * `completedCount === 0` branch it means exactly "some action progress, no
 * task finished".
 */
export function getTrialExtensionPhase({
  allTasksComplete,
  completedCount,
  totalCount,
  hasStartedActions,
}: TrialExtensionProgress): TrialExtensionPhase {
  if (allTasksComplete) return 'earned';
  if (completedCount === 0) return hasStartedActions ? 'actionsStarted' : 'notStarted';
  if (totalCount - completedCount === 1) return 'oneLeft';
  return 'inProgress';
}

export type Heidi2ClinicianCopyOptions = {
  isHeidi2Clinician?: boolean;
  days?: number;
};

export function getModalTitle(
  progress: TrialExtensionProgress,
  t: TranslateFunction,
  fallbackTitle: string,
  intl?: IntlShape,
  { isHeidi2Clinician = false, days = 14 }: Heidi2ClinicianCopyOptions = {}
): string {
  const phase = getTrialExtensionPhase(progress);
  if (isHeidi2Clinician && intl) {
    return phase === 'earned'
      ? intl.formatMessage(messages.modalTitleEarnedHeidi2Clinician, { days })
      : fallbackTitle;
  }
  switch (phase) {
    case 'earned':
      return t('trialExtension.modalHeadlineEarned');
    case 'oneLeft':
      return t('trialExtension.modalHeadline2Of3');
    default:
      return fallbackTitle;
  }
}

export function getModalSubtitle(
  progress: TrialExtensionProgress,
  t: TranslateFunction,
  intl: IntlShape,
  tierLabel: string,
  { isHeidi2Clinician = false, days = 14 }: Heidi2ClinicianCopyOptions = {}
): string {
  const { completedCount, totalCount } = progress;
  const phase = getTrialExtensionPhase(progress);
  if (isHeidi2Clinician) {
    return phase === 'earned'
      ? intl.formatMessage(messages.modalBodyEarnedHeidi2Clinician, { days })
      : intl.formatMessage(messages.modalBodyActionsStartedHeidi2Clinician);
  }
  switch (phase) {
    case 'earned':
      return t('trialExtension.modalBodyEarned', { tier: tierLabel });
    case 'actionsStarted':
      return t('trialExtension.modalBodyInProgress', { tier: tierLabel });
    case 'notStarted':
      return intl.formatMessage(messages.modalBodyNotStarted, { tier: tierLabel });
    case 'oneLeft':
      return t('trialExtension.modalBody2Of3', { tier: tierLabel });
    case 'inProgress':
      return intl.formatMessage(messages.modalBodyProgress, {
        completed: completedCount,
        total: totalCount,
        tier: tierLabel,
      });
  }
}

export function getModalFooter(
  progress: TrialExtensionProgress,
  t: TranslateFunction,
  tierLabel: string,
  intl?: IntlShape,
  { isHeidi2Clinician = false, days = 14 }: Heidi2ClinicianCopyOptions = {}
): string {
  const isEarned = getTrialExtensionPhase(progress) === 'earned';
  if (isHeidi2Clinician && intl) {
    return isEarned
      ? intl.formatMessage(messages.hoverCardSubtitleCompleteHeidi2Clinician, { days })
      : intl.formatMessage(messages.modalFooterNoActionHeidi2Clinician);
  }
  return isEarned
    ? t('trialExtension.modalFooterEarned', { tier: tierLabel })
    : t('trialExtension.modalFooterNoAction', { tier: tierLabel });
}

export function getHoverCardSubtitle(
  progress: TrialExtensionProgress,
  t: TranslateFunction,
  intl: IntlShape,
  tierLabel: string,
  { isHeidi2Clinician = false, days = 14 }: Heidi2ClinicianCopyOptions = {}
): string {
  const { completedCount, totalCount } = progress;
  const remaining = totalCount - completedCount;
  const heidi2StepsSubtitle = (count: number) =>
    intl.formatMessage(messages.hoverCardSubtitleNotStartedHeidi2Clinician, {
      count,
    });

  switch (getTrialExtensionPhase(progress)) {
    case 'earned':
      return isHeidi2Clinician
        ? intl.formatMessage(messages.hoverCardSubtitleCompleteHeidi2Clinician, { days })
        : t('trialExtension.hoverCardSubtitleComplete');
    case 'notStarted':
      return isHeidi2Clinician
        ? heidi2StepsSubtitle(totalCount)
        : intl.formatMessage(messages.hoverCardSubtitleNotStarted);
    case 'actionsStarted':
      return isHeidi2Clinician
        ? heidi2StepsSubtitle(remaining)
        : t('trialExtension.hoverCardSubtitleInProgress');
    case 'oneLeft':
      return isHeidi2Clinician
        ? heidi2StepsSubtitle(remaining)
        : t('trialExtension.hoverCardSubtitle2Of3');
    case 'inProgress':
      return isHeidi2Clinician
        ? heidi2StepsSubtitle(remaining)
        : t('trialExtension.hoverCardSubtitleProgress', {
            completed: completedCount,
            total: totalCount,
            tier: tierLabel,
          });
  }
}

export function getHoverCardCta(
  progress: TrialExtensionProgress,
  t: TranslateFunction,
  intl: IntlShape,
  { isHeidi2Clinician = false }: { isHeidi2Clinician?: boolean } = {}
): string {
  switch (getTrialExtensionPhase(progress)) {
    case 'earned':
      return t('trialExtension.hoverCardCtaDetails');
    case 'notStarted':
      return t('trialExtension.hoverCardCtaGetStarted');
    // Singular CTA only when exactly one task is left, whatever the total.
    // Heidi 2 Clinician keeps one "steps" CTA across remaining phases.
    case 'oneLeft':
      return isHeidi2Clinician
        ? intl.formatMessage(messages.hoverCardCtaRemainingHeidi2Clinician)
        : t('trialExtension.hoverCardCtaRemainingTask');
    case 'actionsStarted':
    case 'inProgress':
      return isHeidi2Clinician
        ? intl.formatMessage(messages.hoverCardCtaRemainingHeidi2Clinician)
        : t('trialExtension.hoverCardCtaRemaining');
  }
}
